import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, FileText, Loader2 } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api, apiBaseUrl, getErrorMessage } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const STATUSES = ["pending_review", "approved", "rejected"];

function kycMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  if (path.startsWith("/uploads/")) {
    const base = apiBaseUrl();
    if (base && !/localhost|127\.0\.0\.1/i.test(base)) return `${base}${path}`;
    const proxy = (process.env.REACT_APP_PROXY_TARGET || "https://api.paisacx.io").replace(/\/$/, "");
    return `${proxy}${path}`;
  }
  const base = apiBaseUrl() || "";
  return `${base}${path}`;
}

export default function AdminKyc() {
  const nav = useNavigate();
  const { admin } = useAdminAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending_review");
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [actionErr, setActionErr] = useState("");
  const [reason, setReason] = useState("");
  const pager = useAdminPager();

  useEffect(() => {
    if (admin === false) nav("/admin/login");
  }, [admin, nav]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/kyc/list", {
        params: { status, skip: pager.skip, limit: pager.limit },
      });
      const { items, total } = unwrapAdminList(data);
      setList(items);
      pager.setTotal(total);
    } catch (e) {
      setActionErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => {
    if (admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, admin, pager.page]);

  async function openUser(u) {
    setSelected(u);
    setDocs([]);
    setReason("");
    setActionErr("");
    try {
      const { data } = await api.get(`/api/admin/kyc/${u.user.id}/documents`);
      setDocs(data);
    } catch (e) {
      setActionErr(getErrorMessage(e));
    }
  }

  async function approve() {
    if (!selected) return;
    setActionErr("");
    try {
      await api.post(`/api/admin/kyc/${selected.user.id}/approve`);
      setSelected(null);
      load();
    } catch (e) {
      setActionErr(getErrorMessage(e));
    }
  }

  async function reject() {
    if (!selected) return;
    setActionErr("");
    if (!reason.trim()) return setActionErr("Please provide a reason");
    try {
      const form = new FormData();
      form.append("reason", reason);
      await api.post(`/api/admin/kyc/${selected.user.id}/reject`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelected(null);
      load();
    } catch (e) {
      setActionErr(getErrorMessage(e));
    }
  }

  if (!admin) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  return (
    <AdminShell title="KYC review" subtitle="Aadhaar, PAN, face match and bank verification queue.">
      <main>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Status</span>
          <div className="flex gap-1">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                data-testid={`admin-filter-${s}`}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium capitalize transition ${
                  status === s
                    ? "border-primary/40 bg-primary/15 text-primary"
                    : "border-border bg-surface-2 text-muted-foreground hover:text-foreground"
                }`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface/50">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">
              <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
            </div>
          ) : list.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-[14px]">No submissions.</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-surface text-muted-foreground text-left">
                <tr>
                  <th className="px-5 py-3 font-semibold">User</th>
                  <th className="px-5 py-3 font-semibold">Full name</th>
                  <th className="px-5 py-3 font-semibold">Mobile</th>
                  <th className="px-5 py-3 font-semibold">Submitted</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((row) => (
                  <tr key={row.user.id} data-testid={`admin-row-${row.user.id}`}>
                    <td className="px-5 py-3 text-white font-medium">{row.user.email}</td>
                    <td className="px-5 py-3 text-muted-foreground">{row.profile?.full_name || "—"}</td>
                    <td className="px-5 py-3 text-muted-foreground">{row.user.mobile_number || "—"}</td>
                    <td className="px-5 py-3 text-muted-foreground">
                      {row.user.kyc_submitted_at?.slice(0, 10) || "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => openUser(row)}
                        className="text-primary font-semibold hover:underline"
                        data-testid={`admin-review-${row.user.id}`}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <AdminPager
            page={pager.page}
            total={pager.total}
            pageSize={pager.pageSize}
            loading={loading}
            onPage={pager.setPage}
            testId="kyc-pager"
          />
        </div>

      {/* Review modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setSelected(null)}
          data-testid="admin-review-modal"
        >
          <div
            className="bg-surface w-full max-w-[820px] max-h-[90vh] overflow-y-auto rounded-xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[22px] font-semibold text-white">
                  {selected.profile?.full_name || selected.user.email}
                </h2>
                <p className="text-[13px] text-muted-foreground mt-1">{selected.user.email}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-muted-foreground">
                <X size={20} />
              </button>
            </div>

            {selected.profile && (
              <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
                {[
                  ["DOB", selected.profile.date_of_birth],
                  ["Gender", selected.profile.gender],
                  ["Nationality", selected.profile.nationality],
                  ["Occupation", selected.profile.occupation],
                  ["Annual income", selected.profile.annual_income_range],
                  ["Source of funds", selected.profile.source_of_funds],
                  ["PAN", selected.profile.pan_number || "—"],
                  ["PEP", selected.profile.pep_declaration ? "Yes" : "No"],
                  ["City", selected.profile.address?.city],
                  ["State", selected.profile.address?.state],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-muted-foreground text-[11px] uppercase tracking-wider">{k}</div>
                    <div className="text-white font-medium">{v || "—"}</div>
                  </div>
                ))}
              </div>
            )}

            {selected.profile?.verification_mode === "truid" && (
              <div className="mt-6 rounded-lg border border-sky-500/30 bg-sky-500/10 p-4 text-[13px] space-y-2">
                <div className="font-semibold text-sky-300">TruID verification</div>
                <div className="text-foreground">Status: {selected.profile.truid_status || "—"}</div>
                {selected.profile.verification?.engine && (
                  <div className="text-muted-foreground">Engine: {selected.profile.verification.engine}</div>
                )}
                {selected.profile.digilocker_request_id && (
                  <div className="text-muted-foreground font-mono text-[11px]">State: {selected.profile.digilocker_request_id}</div>
                )}
                {selected.profile.truid?.aadhaar?.aadhaar_number && (
                  <div className="text-muted-foreground">Aadhaar: {selected.profile.truid.aadhaar.aadhaar_number}</div>
                )}
                {(selected.profile.pan_number || selected.profile.truid?.pan?.pan_number) && (
                  <div className="text-muted-foreground">
                    PAN: {selected.profile.pan_number || selected.profile.truid?.pan?.pan_number}
                    {selected.profile.pan_verify?.registered_name
                      ? ` · ${selected.profile.pan_verify.registered_name}`
                      : ""}
                  </div>
                )}
                {selected.profile.pan_verify && (
                  <div className={selected.profile.pan_verify.verified ? "text-emerald-300" : "text-red-300"}>
                    PAN Detailed: {selected.profile.pan_verify.verified ? "passed" : "failed"}
                  </div>
                )}
                {selected.profile.face_match && (
                  <div className={selected.profile.face_match.verified ? "text-emerald-300" : "text-red-300"}>
                    Face match: {selected.profile.face_match.verified ? "passed" : "failed"}
                    {selected.profile.face_match.match_percentage != null && ` (${selected.profile.face_match.match_percentage}%)`}
                  </div>
                )}
                {(() => {
                  const aadhaarPhoto =
                    selected.profile.aadhaar_photo_url
                    || selected.profile.verification?.aadhaar_photo_stored_url
                    || selected.profile.truid?.aadhaar?.photo_url
                    || selected.profile.document_front_url;
                  const aadhaarPdf =
                    selected.profile.aadhaar_pdf_url
                    || selected.profile.verification?.aadhaar_pdf_stored_url
                    || selected.profile.truid?.aadhaar?.pdf_url;
                  const selfie = selected.profile.selfie_url;
                  if (!aadhaarPhoto && !selfie && !aadhaarPdf) {
                    return selected.profile.truid?.aadhaar?.photo_missing ? (
                      <div className="mt-3 text-[12px] text-amber-300">
                        DigiLocker did not return an Aadhaar face photo for this user.
                      </div>
                    ) : null;
                  }
                  return (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {aadhaarPhoto ? (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Aadhaar photo</div>
                        <img
                          src={kycMediaUrl(aadhaarPhoto)}
                          alt="Aadhaar"
                          className="rounded-lg border border-line max-h-40 w-full object-contain bg-background"
                        />
                      </div>
                    ) : null}
                    {selfie ? (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Selfie</div>
                        <img
                          src={kycMediaUrl(selfie)}
                          alt="Selfie"
                          className="rounded-lg border border-line max-h-40 w-full object-contain bg-background"
                        />
                      </div>
                    ) : null}
                    {aadhaarPdf ? (
                      <a
                        href={kycMediaUrl(aadhaarPdf)}
                        target="_blank"
                        rel="noreferrer"
                        className="col-span-2 text-[12px] text-sky-300 hover:underline"
                      >
                        Open eAadhaar PDF
                      </a>
                    ) : null}
                  </div>
                  );
                })()}
              </div>
            )}

            {selected.profile?.verification_mode === "signzy" && (
              <div className="mt-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-[13px] space-y-2">
                <div className="font-semibold text-emerald-300">Signzy verification</div>
                <div className="text-foreground">Status: {selected.profile.signzy_status || "—"}</div>
                {selected.profile.verification?.engine && (
                  <div className="text-muted-foreground">Engine: {selected.profile.verification.engine}</div>
                )}
                {selected.profile.digilocker_request_id && (
                  <div className="text-muted-foreground font-mono text-[11px]">Request: {selected.profile.digilocker_request_id}</div>
                )}
                {selected.profile.face_match && (
                  <div className={selected.profile.face_match.verified ? "text-emerald-300" : "text-red-300"}>
                    Face match: {selected.profile.face_match.verified ? "passed" : "failed"}
                    {selected.profile.face_match.match_percentage != null && ` (${selected.profile.face_match.match_percentage}%)`}
                  </div>
                )}
                {selected.profile.pan_verify && (
                  <div className={selected.profile.pan_verify.verified ? "text-emerald-300" : "text-amber-300"}>
                    PAN verify: {selected.profile.pan_verify.verified ? "passed" : "failed"}
                  </div>
                )}
              </div>
            )}

            <div className="mt-8">
              <h3 className="text-[15px] font-semibold text-white mb-3">Documents</h3>
              <div className="grid grid-cols-2 gap-3">
                {docs.map((d) => (
                  <a
                    key={d.doc_type}
                    href={`${apiBaseUrl()}/api/admin/kyc/${selected.user.id}/documents/${d.doc_type}/file`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-surface-2 transition"
                    data-testid={`admin-doc-${d.doc_type}`}
                  >
                    <FileText size={18} className="text-primary" />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-white capitalize">
                        {d.doc_type.replace(/_/g, " ")}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{d.file_name}</div>
                    </div>
                  </a>
                ))}
                {docs.length === 0 && <div className="text-muted-foreground text-[13px]">No documents.</div>}
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Rejection reason (required to reject)"
                data-testid="admin-reason"
                className="w-full p-3 border border-line bg-background text-white placeholder:text-dim rounded-lg text-[13px] focus:border-primary outline-none min-h-[70px]"
              />
              {actionErr && (
                <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                  {actionErr}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={reject}
                  data-testid="admin-reject"
                  className="flex-1 py-3 rounded-lg bg-red-500 text-white font-semibold text-[14px] hover:bg-red-600 flex items-center justify-center gap-2"
                >
                  <X size={16} /> Reject
                </button>
                <button
                  onClick={approve}
                  data-testid="admin-approve"
                  className="flex-1 py-3 rounded-lg bg-emerald-500 text-white font-semibold text-[14px] hover:bg-emerald-600 flex items-center justify-center gap-2"
                >
                  <Check size={16} /> Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </main>
    </AdminShell>
  );
}
